// Pre-IFR Agent: the job cards received from the client (Tata), where each one stands, and what
// the customer said. Built from the Advisor's Desk pieces (components/msd/ui): a card of rows, status
// pills, and a sheet that slides over the right edge for one job card.

import { useEffect, useState } from "react";
import { formatWhen } from "../lib/dashboardTokens";
import { usePreIfrJobCard, usePreIfrJobCards } from "../lib/api";
import { firstMessageText, stageText } from "../lib/preIfr";
import { Avatar, CARD, Empty, LBL, Page, PageHead, Pill, SECL, SearchBox, Sheet, Tag, cx } from "../components/msd/ui";
import { TablePagination } from "../components/TablePagination";

const PAGE_SIZE = 25;

// Capsules are neutral; the colour lives in the dot.
const STATUS_TONE = { no_reply: null, happy: "green", unhappy: "red", resolved: "green", closed: null };
const CONCERN_TONE = { logged: "orange", in_progress: "orange", resolved: "green", closed: null };

function StatusTag({ status, label }) {
  return (
    <Tag tone={STATUS_TONE[status]} data-testid={`pre-ifr-status-${status}`}>
      {label}
    </Tag>
  );
}

function Fact({ label, children }) {
  return (
    <div className="min-w-0">
      <div className={LBL}>{label}</div>
      <div className="truncate text-[14px] text-msd-ink">{children || "—"}</div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section>
      <div className={cx(SECL, "mb-2 uppercase")}>{title}</div>
      {children}
    </section>
  );
}

// The escalation levels of one concern, down a line: a dot per level, the role beside it, the time at the end.
function Levels({ n, levels }) {
  if (!levels?.length) {
    return (
      <div className="text-[13px] text-msd-muted" data-testid={`pre-ifr-levels-empty-${n}`}>
        Not escalated yet.
      </div>
    );
  }
  return (
    <ol className="m-0 list-none p-0" data-testid={`pre-ifr-levels-${n}`}>
      {levels.map((l, i) => (
        <li key={l.level} className="relative flex items-baseline gap-3 pb-3 pl-5 last:pb-0" data-testid={`pre-ifr-level-${n}-${l.level}`}>
          {i < levels.length - 1 && <span className="absolute bottom-0 left-[3px] top-[10px] w-px bg-msd-line2" aria-hidden="true" />}
          <span className="absolute left-0 top-[7px] h-[7px] w-[7px] rounded-full bg-msd-royal" aria-hidden="true" />
          <span className="min-w-0 grow text-[14px]">
            <b className="font-semibold text-msd-ink">Level {l.level}</b>
            {l.role_label && <span className="text-msd-muted"> · {l.role_label}</span>}
          </span>
          <span className="shrink-0 text-[12.5px] tabular-nums text-msd-muted">{formatWhen(l.at)}</span>
        </li>
      ))}
    </ol>
  );
}

function Concern({ n, total, c }) {
  return (
    <div className="rounded-2xl bg-msd-soft p-[14px]" data-testid={`pre-ifr-concern-${n}`}>
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-[15px] text-msd-ink">{c.id}</b>
        <Tag tone={CONCERN_TONE[c.stage]} className="capitalize">
          {stageText(c.stage)}
        </Tag>
        <span className="ml-auto text-[12.5px] tabular-nums text-msd-muted">{formatWhen(c.raised_at)}</span>
      </div>
      {c.reason && (
        <div className="mt-3" data-testid={`pre-ifr-reason-${n}`}>
          <div className={LBL}>Reason</div>
          <div className="text-[14px] text-msd-ink">{c.reason}</div>
        </div>
      )}
      <div className={cx(SECL, "mb-2 mt-4 uppercase")}>{total > 1 ? `Escalations · concern ${n}` : "Escalations"}</div>
      <Levels n={n} levels={c.levels} />
    </div>
  );
}

function Detail({ row, onClose }) {
  const { data, isLoading } = usePreIfrJobCard(row?.id);
  const card = data?.job_card || row;
  return (
    <Sheet open={!!row} onClose={onClose} title={row ? card.customer_name || card.customer_number : ""} testid="pre-ifr-drawer">
      {row && (
        <>
          <div className="-mt-1 flex flex-wrap items-center gap-2">
            <span className="text-[14px] text-msd-muted">
              {card.reg_no} · {card.jc_id}
            </span>
            <StatusTag status={card.status} label={card.status_label} />
            {card.ladder?.label && (
              <span className="text-[13px] text-msd-muted" data-testid="pre-ifr-ladder">
                {card.ladder.label}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Fact label="Customer">{card.customer_name}</Fact>
            <Fact label="Mobile">{card.customer_number}</Fact>
            <Fact label="Advisor">{[card.advisor_name, card.advisor_number].filter(Boolean).join(" · ")}</Fact>
            <Fact label="Workshop">{card.workshop_location || "Not set"}</Fact>
            <Fact label="Dealer code">{card.dealer_code}</Fact>
            <Fact label="Received">{formatWhen(card.received_at)}</Fact>
          </div>

          {isLoading ? (
            <div className="text-[14px] text-msd-muted">Loading…</div>
          ) : (
            <>
              <Block title="Response">
                {data?.response?.answer ? (
                  <div className="flex items-center gap-2" data-testid="pre-ifr-response">
                    <StatusTag status={data.response.answer} label={data.response.answer === "happy" ? "Happy" : "Unhappy"} />
                    <span className="text-[12.5px] tabular-nums text-msd-muted">{formatWhen(data.response.at)}</span>
                  </div>
                ) : (
                  <div className="text-[14px] text-msd-muted" data-testid="pre-ifr-response-empty">
                    No reply yet.
                  </div>
                )}
              </Block>

              {data?.concerns?.length > 0 && (
                <Block title={data.concerns.length > 1 ? `Concerns · ${data.concerns.length}` : "Concern"}>
                  <div className="flex flex-col gap-3">
                    {data.concerns.map((c, i) => (
                      <Concern key={c.id || i} n={i + 1} total={data.concerns.length} c={c} />
                    ))}
                  </div>
                </Block>
              )}
            </>
          )}
        </>
      )}
    </Sheet>
  );
}

// One job card in the list. Wide: customer, workshop, first message, status, concern, received. Narrow: wraps.
const ROW =
  "grid w-full cursor-pointer grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_96px] items-center gap-4 rounded-xl border-0 bg-transparent p-[10px] text-left text-inherit hover:bg-msd-soft max-[900px]:grid-cols-2 [&+&]:shadow-[0_-1px_0_var(--line)]";

function JobCardRow({ r, onOpen }) {
  const concern = r.complaint;
  return (
    <button type="button" className={ROW} data-testid={`pre-ifr-row-${r.id}`} onClick={() => onOpen(r)}>
      <span className="flex min-w-0 items-center gap-3 max-[900px]:col-span-2">
        <Avatar name={r.customer_name} size={40} />
        <span className="min-w-0">
          <b className="block truncate text-[15px] font-semibold text-msd-ink">{r.reg_no || "—"}</b>
          <span className="block truncate text-[13px] text-msd-muted">
            {[r.customer_name, r.jc_id].filter(Boolean).join(" · ")}
          </span>
        </span>
      </span>
      <span className="truncate text-[14px] text-msd-ink2">{r.workshop_location || "—"}</span>
      <span className="truncate text-[14px] text-msd-ink2">{firstMessageText(r.first_message, formatWhen)}</span>
      <span>
        <StatusTag status={r.status} label={r.status_label} />
      </span>
      <span className="min-w-0 text-[14px] text-msd-ink2">
        {concern ? (
          <>
            <span className="block truncate">{concern.ref}</span>
            <span className="block truncate text-[12.5px] capitalize text-msd-muted">{stageText(concern.stage)}</span>
          </>
        ) : (
          <span className="text-msd-faint">—</span>
        )}
      </span>
      <span className="text-[13px] tabular-nums text-msd-muted">{formatWhen(r.received_at)}</span>
    </button>
  );
}

const HEAD =
  "grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_96px] gap-4 px-[10px] pb-1 pt-2 max-[900px]:hidden";

export default function PreIfr() {
  const [search, setSearch] = useState("");
  const [typed, setTyped] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);

  // Search runs a moment after typing stops, not on every key.
  useEffect(() => {
    const t = setTimeout(() => setSearch(typed.trim()), 300);
    return () => clearTimeout(t);
  }, [typed]);
  useEffect(() => setPage(0), [search, status]);

  const { data, isLoading, isError, error } = usePreIfrJobCards({
    search,
    status,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const rows = data?.rows || [];
  const counts = data?.counts || {};
  const statuses = data?.statuses || [];
  const total = data?.total || 0;
  const all = Object.values(counts).reduce((a, b) => a + b, 0);

  const chip = (key, label, n) => (
    <Pill key={key || "all"} on={status === key} count={n} onClick={() => setStatus(key)} data-testid={`pre-ifr-chip-${key || "all"}`}>
      {label}
    </Pill>
  );

  return (
    <Page data-testid="pre-ifr-page">
      <PageHead title="Pre-IFR Agent" sub={`${all} job card${all === 1 ? "" : "s"} from the client`}>
        <SearchBox value={typed} onChange={setTyped} placeholder="Search job card, vehicle, customer, concern" testid="pre-ifr-search" />
      </PageHead>

      <div className="flex flex-wrap gap-2">
        {chip("", "All", all)}
        {statuses.map((s) => chip(s.key, s.label, counts[s.key] || 0))}
      </div>

      {isLoading ? (
        <div className={cx(CARD, "py-12 text-center text-[14px] text-msd-muted")} data-testid="pre-ifr-loading">
          Loading…
        </div>
      ) : isError ? (
        <div className={cx(CARD, "py-12 text-center text-[14px] text-msd-brick")} data-testid="pre-ifr-error">
          {error?.response?.data?.detail || "Could not load the job cards."}
        </div>
      ) : total === 0 ? (
        <div data-testid="pre-ifr-empty">
          <Empty>{all === 0 ? "No job cards have been received yet." : "No job cards match."}</Empty>
        </div>
      ) : (
        <div className={cx(CARD, "px-[10px] py-2")} data-testid="pre-ifr-table-card">
          <div className={cx(HEAD, SECL, "uppercase")} aria-hidden="true">
            <span>Job card</span>
            <span>Workshop</span>
            <span>First message</span>
            <span>Status</span>
            <span>Concern</span>
            <span>Received</span>
          </div>
          <div data-testid="pre-ifr-table">
            {rows.map((r) => (
              <JobCardRow key={r.id} r={r} onOpen={setSelected} />
            ))}
          </div>
          <TablePagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
          {data?.truncated && (
            <p className="px-[10px] pb-2 text-[13px] text-msd-muted" data-testid="pre-ifr-truncated">
              Showing the newest 500 job cards.
            </p>
          )}
        </div>
      )}

      <Detail row={selected} onClose={() => setSelected(null)} />
    </Page>
  );
}
